// Consola de WhatsApp — herramienta interna (superuser) para grabar los videos
// de Meta App Review. Demuestra los dos permisos que Meta revisa:
//   · whatsapp_business_messaging  → enviar un mensaje que llega al teléfono
//   · whatsapp_business_management → listar / crear plantillas
// Todo contra un número de PRUEBA de Meta ya conectado a la app Orión.

import { useEffect, useState } from 'react';
import { MessageSquare, Send, RefreshCw, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  whatsAppConsoleApi as api,
  type DemoConfig,
  type WaTemplate,
} from '../api';

type Status = { kind: 'ok' | 'err'; text: string } | null;

export function WhatsAppConsolePage() {
  const [config, setConfig] = useState<DemoConfig | null>(null);

  // ── Enviar mensaje ─────────────────────────────
  const [to, setTo] = useState('525611680933');
  const [body, setBody] = useState('Hola 👋 Este es un mensaje enviado desde la plataforma Orion.');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<Status>(null);

  // ── Plantillas ─────────────────────────────────
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplBody, setTplBody] = useState('Hola {{1}}, gracias por escribirnos. ¿En qué te ayudamos?');
  const [creatingTpl, setCreatingTpl] = useState(false);
  const [tplStatus, setTplStatus] = useState<Status>(null);

  useEffect(() => {
    api.getConfig().then(setConfig).catch(() => setConfig(null));
    refreshTemplates();
  }, []);

  async function refreshTemplates() {
    setLoadingTpl(true);
    try {
      setTemplates(await api.listTemplates());
    } catch (e) {
      setTplStatus({ kind: 'err', text: (e as Error).message });
    } finally {
      setLoadingTpl(false);
    }
  }

  async function handleSend(asTemplate: boolean) {
    setSending(true);
    setSendStatus(null);
    try {
      const res = await api.send(
        asTemplate
          ? { to, template_name: 'hello_world', language_code: 'en_US' }
          : { to, body },
      );
      const id =
        (res as { messages?: { id?: string }[] }).messages?.[0]?.id ?? 'enviado';
      setSendStatus({ kind: 'ok', text: `Mensaje enviado ✓  (wa id: ${id})` });
    } catch (e) {
      setSendStatus({ kind: 'err', text: (e as Error).message });
    } finally {
      setSending(false);
    }
  }

  async function handleCreateTemplate() {
    if (!tplName.trim()) {
      setTplStatus({ kind: 'err', text: 'Ponle un nombre a la plantilla' });
      return;
    }
    setCreatingTpl(true);
    setTplStatus(null);
    try {
      const res = await api.createTemplate({
        name: tplName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        body_text: tplBody,
        category: 'MARKETING',
        language_code: 'es_MX',
      });
      const id = (res as { id?: string }).id ?? 'creada';
      setTplStatus({ kind: 'ok', text: `Plantilla creada ✓  (id: ${id})` });
      setTplName('');
      refreshTemplates();
    } catch (e) {
      setTplStatus({ kind: 'err', text: (e as Error).message });
    } finally {
      setCreatingTpl(false);
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Encabezado */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          <MessageSquare size={22} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">WhatsApp Console</h1>
          <p className="text-sm text-muted-foreground">
            Envía mensajes y gestiona plantillas de WhatsApp desde la plataforma.
          </p>
        </div>
      </div>

      {config && (
        <div className="text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 bg-muted/40">
          Número conectado · phone id <code className="text-foreground">{config.phone_number_id}</code>
          {' · '}WABA <code className="text-foreground">{config.waba_id}</code>
        </div>
      )}

      {/* ── Enviar mensaje ── */}
      <section className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Send size={16} /> Enviar mensaje
        </h2>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Destinatario (WhatsApp)</label>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="525611680933"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Mensaje</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleSend(false)}
            disabled={sending || !to || !body}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 hover:opacity-90"
          >
            <Send size={15} /> {sending ? 'Enviando…' : 'Enviar mensaje'}
          </button>
          <button
            onClick={() => handleSend(true)}
            disabled={sending || !to}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground disabled:opacity-50 hover:bg-muted"
            title="Envía la plantilla pre-aprobada hello_world (funciona sin ventana de 24h)"
          >
            Enviar plantilla hello_world
          </button>
        </div>

        {sendStatus && <StatusLine status={sendStatus} />}
      </section>

      {/* ── Plantillas ── */}
      <section className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Plantillas de mensaje</h2>
          <button
            onClick={refreshTemplates}
            disabled={loadingTpl}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground disabled:opacity-50 hover:bg-muted"
          >
            <RefreshCw size={13} className={loadingTpl ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>

        <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
          {templates.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              {loadingTpl ? 'Cargando…' : 'Sin plantillas todavía.'}
            </div>
          )}
          {templates.map((t) => (
            <div key={t.name} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="font-medium text-foreground">{t.name}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {t.language && <span>{t.language}</span>}
                {t.category && <span className="px-1.5 py-0.5 rounded bg-muted">{t.category}</span>}
                {t.status && (
                  <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">{t.status}</span>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Crear plantilla */}
        <div className="space-y-2 pt-2 border-t border-border">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Crear plantilla
          </h3>
          <input
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="nombre_de_la_plantilla"
          />
          <textarea
            value={tplBody}
            onChange={(e) => setTplBody(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
          />
          <button
            onClick={handleCreateTemplate}
            disabled={creatingTpl || !tplName.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 hover:opacity-90"
          >
            <Plus size={15} /> {creatingTpl ? 'Creando…' : 'Crear plantilla'}
          </button>
        </div>

        {tplStatus && <StatusLine status={tplStatus} />}
      </section>
    </div>
  );
}

function StatusLine({ status }: { status: NonNullable<Status> }) {
  const ok = status.kind === 'ok';
  return (
    <div
      className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${
        ok
          ? 'bg-green-500/10 text-green-600 dark:text-green-400'
          : 'bg-destructive/10 text-destructive'
      }`}
    >
      {ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
      {status.text}
    </div>
  );
}
